import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  EyeOutlined,
  FileTextOutlined,
  UserOutlined,
  CarOutlined,
  CalendarOutlined,
  DollarOutlined,
  CreditCardOutlined,
  WalletOutlined,
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  SafetyOutlined,
  EnvironmentOutlined,
  FilePdfOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  message,
  Modal,
  Select,
  Row,
  Col,
  DatePicker,
  Statistic,
  Descriptions,
  Tabs,
  Empty,
  Badge,
  Divider,
  Popconfirm,
  Grid,
  Pagination
} from 'antd';
import * as XLSX from 'xlsx';
import {
  getParties,
  getBillsByParty,
  getReceipts,
  getVehicleInsurancesByParty,
  deleteVehicleInsurance
} from '../Services/Services';
import html2pdf from 'html2pdf.js';
import logo from '../Logo.png';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const PartyLedger = ({ onEditVehicle }) => {
  const [parties, setParties] = useState([]);
  const [selectedParty, setSelectedParty] = useState(null);
  const [bills, setBills] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedVehicle, setSelectedVehicle] = useState('all');
  const [activeTab, setActiveTab] = useState('all');

  // For mobile expand/collapse
  const [expandedItems, setExpandedItems] = useState({});
  const [allPage, setAllPage] = useState(1);
  const [billPage, setBillPage] = useState(1);
  const [receiptPage, setReceiptPage] = useState(1);
  const [vehiclePage, setVehiclePage] = useState(1);

  const mobilePageSize = 5;

  const [vehicleReportData, setVehicleReportData] = useState([]);
  const [vehicleLoading, setVehicleLoading] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Toggle expand on mobile cards
  const toggleExpand = (id, type) => {
    const key = `${type}_${id}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    fetchParties();
  }, []);

  const fetchParties = async () => {
    try {
      const response = await getParties();
      setParties(response.results || response || []);
    } catch (error) {
      console.error('Error fetching parties:', error);
      message.error('Failed to fetch parties');
    }
  };

  const fetchPartyLedger = async (partyId) => {
    setLoading(true);
    try {
      const [billsData, receiptsData, vehiclesData] = await Promise.all([
        getBillsByParty(partyId),
        getReceipts({ party_id: partyId }),
        getVehicleInsurancesByParty(partyId)
      ]);

      const bills = billsData.results || [];
      const receipts = receiptsData.results || [];

      const receiptsWithVehicle = receipts.map(receipt => {
        const matchedBill = bills.find(
          bill => bill.bill_id === receipt.bill_no
        );

        return {
          ...receipt,
          vehicle_no:
            matchedBill?.items?.[0]?.vehicle_no ||
            matchedBill?.items?.[0]?.vehicleNo ||
            "-"
        };
      });

      setBills(bills);
      setReceipts(receiptsWithVehicle);
      setVehicles(vehiclesData.results || []);
      setVehicleReportData(vehiclesData.results || []);

      message.success(`Loaded ${billsData.results?.length || 0} bills, ${receiptsData.results?.length || 0} receipts, ${vehiclesData.results?.length || 0} vehicles`);
    } catch (error) {
      console.error('Error fetching party ledger:', error);
      message.error('Failed to fetch party ledger');
    } finally {
      setLoading(false);
    }
  };

  const handlePartySelect = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    setSelectedParty(party);
    if (partyId) {
      fetchPartyLedger(partyId);
    } else {
      setBills([]);
      setReceipts([]);
      setVehicles([]);
      setVehicleReportData([]);
    }
  };

  const isExpired = (dateString) => {
    if (!dateString || dateString === '—' || dateString === 'N/A') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(dateString);
    return expiryDate < today;
  };

  const handleVehicleDelete = async (vehicleNo, id) => {
    if (window.confirm(`Delete ${vehicleNo}?`)) {
      setVehicleLoading(true);
      try {
        await deleteVehicleInsurance(id);
        message.success(`${vehicleNo} deleted`);
        const vehiclesData = await getVehicleInsurancesByParty(selectedParty?.id);
        setVehicles(vehiclesData.results || []);
        setVehicleReportData(vehiclesData.results || []);
      } catch (error) {
        message.error("Delete failed");
      } finally {
        setVehicleLoading(false);
      }
    }
  };

  const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) return '0.00';
    return parseFloat(amount).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const downloadVehiclePDF = (item) => {
    setPdfLoading(true);
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const formattedTime = currentDate.toLocaleTimeString('en-IN');

    const getStatusHTML = (date) => {
      if (!date || date === '—' || date === 'N/A') {
        return '<span style="background: #fff3e0; color: #f57c00; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">Not Available</span>';
      }
      const expired = isExpired(date);
      if (expired) {
        return '<span style="background: #ffebee; color: #c62828; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">⚠️ Expired</span>';
      }
      return '<span style="background: #e8f5e9; color: #2e7d32; padding: 3px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;">✓ Valid</span>';
    };

    const documentsRows = `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center; width: 10%;">1</td>
        <td style="padding: 8px; width: 30%;"><strong>📄 Tax Validity</strong></td>
        <td style="padding: 8px; width: 30%; text-align: center;">${item.taxValidity || '—'}</td>
        <td style="padding: 8px; width: 30%; text-align: center;">${getStatusHTML(item.taxValidity)}</td>
      </tr>
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center;">2</td>
        <td style="padding: 8px;"><strong>💪 Fitness Validity</strong></td>
        <td style="padding: 8px; text-align: center;">${item.fitnessValidity || '—'}</td>
        <td style="padding: 8px; text-align: center;">${getStatusHTML(item.fitnessValidity)}</td>
      </tr>
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center;">3</td>
        <td style="padding: 8px;"><strong>🛡️ Insurance Validity</strong></td>
        <td style="padding: 8px; text-align: center;">${item.insuranceValidity || '—'}</td>
        <td style="padding: 8px; text-align: center;">${getStatusHTML(item.insuranceValidity)}</td>
      </tr>
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center;">4</td>
        <td style="padding: 8px;"><strong>🌿 Pollution Validity</strong></td>
        <td style="padding: 8px; text-align: center;">${item.pollutionValidity || '—'}</td>
        <td style="padding: 8px; text-align: center;">${getStatusHTML(item.pollutionValidity)}</td>
      </tr>
    `;

    const expiredDocs = [];
    if (item.taxValidity && isExpired(item.taxValidity)) expiredDocs.push('Tax');
    if (item.fitnessValidity && isExpired(item.fitnessValidity)) expiredDocs.push('Fitness');
    if (item.insuranceValidity && isExpired(item.insuranceValidity)) expiredDocs.push('Insurance');
    if (item.pollutionValidity && isExpired(item.pollutionValidity)) expiredDocs.push('Pollution');

    const summaryHTML = expiredDocs.length > 0
      ? `<span style="color: #c62828;">⚠️ ALERT: The following documents have EXPIRED: ${expiredDocs.join(', ')}. Please renew immediately.</span>`
      : `<span style="color: #2e7d32;">✓ All documents are VALID and up-to-date.</span>`;

    const element = document.createElement('div');
    element.innerHTML = `
    <div style="width: 100%; max-width: 1000px; margin: 0 auto; background: #ffffff; font-family: 'Times New Roman', Times, serif; box-sizing: border-box; border: 1px solid #a3a3a3; padding: 15px; border-radius: 4px;">
      
      <div style="height: 5px; background: linear-gradient(90deg, #103f7a 75%, #1c7c34 75%); margin: -15px -15px 15px -15px; border-radius: 4px 4px 0 0;"></div>

      <div style="padding: 5px 10px 15px 10px; background: #ffffff;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 20px;">
          
          <div style="width: 85px; min-width: 85px; display: flex; align-items: center; justify-content: center;">
            <img src="${logo}" style="width: 100%; height: auto; object-fit: contain;" />
          </div>
          
          <div style="flex:1; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #103f7a; letter-spacing: 0.5px; line-height: 1.2;">D.K. TRANSPORT CONSULTANCY</div>
            <div style="font-size: 10.5px; color: #334155; margin-top: 5px; text-transform: uppercase; letter-spacing: 0.3px;">
              PARKING NO. 1 BABLA FUELS KE BAJU T P NAGAR RAWABHATA RAIPUR
            </div>
            <div style="font-size: 10px; color: #1c7c34; font-style: italic; margin-top: 3px;">
              (Deal in all RTO related matters, Insurance - 2 Wheeler, 4 Wheeler, Commercial Vehicles)
            </div>
          </div>
          
          <div style="text-align: right; border-left: 1px solid #cbd5e1; padding-left: 15px; min-width: 150px; font-size: 11px; color: #334155;">
            <div style="font-size: 12px; font-weight: bold; color: #103f7a; margin-bottom: 3px;">Durgesh Kumar Siware</div>
            <div style="margin-bottom: 2px;">📞 79749 97280</div>
            <div>📞 73890 42205</div>
          </div>
        </div>
      </div>

      <div style="text-align: center; background: #103f7a; color: #ffffff; font-size: 13px; font-weight: bold; padding: 4px 0; margin: 5px 10px 15px 10px; letter-spacing: 3px; border-radius: 2px;">
        VEHICLE DOCUMENT REPORT
      </div>

      <div style="margin: 0 10px 12px 10px; border: 1px solid #cbd5e1; border-radius: 3px; background: #fdfdfd; padding: 8px 12px;">
        <div style="display: flex; justify-content: space-between; flex-wrap: wrap; font-size: 11px; font-family: 'Times New Roman', Times, serif;">
          <div><span style="font-weight: bold; color: #525252;">Certificate No:</span> <span style="color: #000000;">DK/VEH/${new Date().getFullYear()}/${item.id || Math.floor(Math.random() * 10000)}</span></div>
          <div><span style="font-weight: bold; color: #525252;">Generated on:</span> <span style="color: #000000;">${formattedDate} at ${formattedTime}</span></div>
          <div><span style="font-weight: bold; color: #525252;">Print Date:</span> <span style="color: #334155;">${formattedDate}</span></div>
        </div>
      </div>

      <div style="margin: 0 10px 15px 10px; padding: 12px; background: #ffffff; border: 1px solid #cbd5e1; border-radius: 3px;">
        <table style="width: 100%; border-collapse: collapse; font-size: 11.5px; font-family: 'Times New Roman', Times, serif;">
          <tr>
            <td style="padding: 4px 0; width: 50%;">
              <span style="font-weight: bold; color: #103f7a;">🚗 Vehicle Number:</span>
              <span style="color: #000000; margin-left: 6px;">${item.vehicleNo}</span>
            </td>
            <td style="padding: 4px 0; width: 50%;">
              <span style="font-weight: bold; color: #103f7a;">👤 Owner Name:</span>
              <span style="color: #000000; margin-left: 6px;">${selectedParty?.partyName || item.ownerName || '—'}</span>
            </td>
          </tr>
          <tr>
            <td style="padding: 6px 0 4px 0; width: 50%;">
              <span style="font-weight: bold; color: #103f7a;">🔢 Chassis Number:</span>
              <span style="color: #000000; margin-left: 6px;">${item.chassisNumber || '—'}</span>
            </td>
            <td style="padding: 6px 0 4px 0; width: 50%;">
              <span style="font-weight: bold; color: #103f7a;">📞 Mobile Number:</span>
              <span style="color: #000000; margin-left: 6px;">${selectedParty?.mobileNumber || item.mobileNumber || '—'}</span>
            </td>
          </tr>
        </table>
      </div>

      <div style="padding: 5px 10px 15px 10px; position: relative;">
        <div style="position: absolute; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-12deg); width: 30%; opacity: 0.03; pointer-events: none;">
          <img src="${logo}" style="width: 100%; height: auto;" />
        </div>

        <table style="width: 100%; border-collapse: collapse; font-size: 11.5px; font-family: 'Times New Roman', Times, serif; border: 1px solid #a3a3a3; margin-bottom: 15px;">
          <thead>
            <tr style="background: #103f7a; color: #ffffff; font-weight: bold;">
              <th style="width: 10%; text-align: center; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">S.No</th>
              <th style="width: 40%; text-align: left; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">DOCUMENT TYPE</th>
              <th style="width: 25%; text-align: center; padding: 8px; border-right: 1px solid #cbd5e1; font-size: 11px;">VALIDITY DATE</th>
              <th style="width: 25%; text-align: center; padding: 8px; font-size: 11px;">STATUS</th>
            </tr>
          </thead>
          <tbody>
            ${documentsRows}
          </tbody>
        </table>

        <div style="background: #f4f7fa; padding: 10px 12px; border-radius: 3px; margin: 15px 0; border-left: 4px solid #103f7a; border-top: 1px solid #e2e8f0; border-right: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0;">
          <div style="font-weight: bold; color: #103f7a; margin-bottom: 5px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px;">📋 Document Summary</div>
          <div style="font-size: 11px; line-height: 1.5; color: #1f2937;">${summaryHTML}</div>
        </div>

        <div style="margin: 15px 0 20px 0; padding: 10px 12px; background: #f7faff; border-left: 4px solid #103f7a; border-radius: 3px; font-size: 11px; display: flex; flex-wrap: wrap; justify-content: space-between; gap: 15px; border-top: 1px solid #e0f2fe; border-right: 1px solid #e0f2fe; border-bottom: 1px solid #e0f2fe;">
          <div><strong style="color: #103f7a;">🏦 A/C NO:</strong> <span style="font-weight: bold;">38473468220</span></div>
          <div><strong style="color: #103f7a;">🏛️ IFSC:</strong> <span style="font-weight: bold;">SBIN0002852</span></div>
          <div><strong style="color: #103f7a;">👤 NAME:</strong> <span style="text-transform: uppercase;">Durgesh Kumar Siware</span></div>
          <div><strong style="color: #103f7a;">📞 MO:</strong> <span>7974997280, 7389042205</span></div>
        </div>

        <div style="display: flex; justify-content: space-between; margin: 35px 15px 10px 15px; font-size: 11px;">
          <div style="text-align: center; width: 30%;">
            <div style="border-bottom: 1px solid #cbd5e1; height: 35px; margin-bottom: 5px;"></div>
            <div style="color: #4b5563;">Customer Signature</div>
          </div>
          <div style="text-align: center; width: 30%;">
            <div style="font-weight: bold; color: #103f7a; margin-bottom: 20px; font-size: 11.5px;">Durgesh Siware</div>
            <div style="border-top: 1px solid #cbd5e1; padding-top: 5px; color: #4b5563;">Authorized Signature</div>
          </div>
        </div>
      </div>

      <div style="background:#0d1e3d; width:100%; border-radius: 2px; overflow: hidden; margin-top: 5px; font-family: Arial, Helvetica, sans-serif;">
        <div style="background:#103f7a; padding:8px 15px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px;">
          <div style="font-size:8.5px; font-weight:bold; color:#ffffff; letter-spacing: 0.3px;">📍 Near Babla Fuels, Piyush Seat Cover Road, Parking No. 1, Transport Nagar, Raipur (C.G.) 493221</div>
          <div style="background:#1c7c34; padding:3px 12px; border-radius:12px; font-size:8.5px; font-weight:bold; color:#ffffff;">✉ durgeshshivare@gmail.com</div>
        </div>
        <div style="background:#ffffff; padding:5px 15px; display:flex; justify-content:flex-end; gap:15px; align-items: center;">
          <svg style="height:16px;" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg"><rect width="100" height="40" rx="3" fill="#0f4c81"/><circle cx="20" cy="20" r="10" fill="white" opacity="0.2"/><text x="60" y="21" font-family="sans-serif" font-weight="bold" font-size="7" fill="white" text-anchor="middle">UNITED INDIA</text><text x="60" y="30" font-family="sans-serif" font-size="5.5" fill="#cbd5e1" text-anchor="middle">INSURANCE</text></svg>
          <svg style="height:16px;" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="17" fill="#103f7a" stroke="#7ac143" stroke-width="1.5"/><path d="M15,12 L18,10 L22,13 L25,11 L28,16 L24,22 L26,28 L20,32 L16,28 L13,24 L16,18 Z" fill="white" opacity="0.85"/></svg>
        </div>
        <div style="background:#051429; padding:6px 15px; text-align:center;">
          <div style="display:flex; flex-wrap:wrap; justify-content:center; gap:3px 12px; font-weight: normal;">
            <span style="font-size:8px; color:#cbd5e1;">ड्राइविंग लाइसेंस</span>
            <span style="font-size:8px; color:#cbd5e1;">इंश्योरेंस</span>
            <span style="font-size:8px; color:#cbd5e1;">रोड टैक्स</span>
            <span style="font-size:8px; color:#cbd5e1;">परमिट</span>
            <span style="font-size:8px; color:#cbd5e1;">नाम ट्रांसफर</span>
            <span style="font-size:8px; color:#cbd5e1;">HSRP नं. प्लेट</span>
            <span style="font-size:8px; color:#cbd5e1;">फिटनेस</span>
          </div>
        </div>
        <div style="font-size:6px; color:#4b5563; text-align:center; padding:3px; background:#020b17; font-weight: bold; letter-spacing: 0.5px;">powered by yashvitech IT solution</div>
      </div>
    </div>
  `;

    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `Vehicle_Report_${item.vehicleNo}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      setPdfLoading(false);
      message.success('PDF downloaded successfully!');
    }).catch((error) => {
      console.error('PDF generation error:', error);
      setPdfLoading(false);
      message.error('Failed to generate PDF');
    });
  };

  const getCombinedTransactions = () => {
    const transactions = [];
    bills.forEach(bill => {
      transactions.push({
        ...bill,
        vehicleNo:
          bill.vehicleNo ||
          bill.vehicle_no ||
          bill.items?.[0]?.vehicle_no ||
          '-',
        type: 'Bill',
        amount: bill.total_amount || 0,
        date: bill.bill_date,
        ref_no: bill.bill_id || bill.id,
        transaction_type: 'debit',
        color: '#1890ff'
      });
    });
    receipts.forEach(receipt => {
      transactions.push({
        ...receipt,
        vehicleNo:
          receipt.vehicleNo ||
          receipt.vehicle_no ||
          '-',
        type: 'Receipt',
        amount: receipt.amount || 0,
        date: receipt.receipt_date,
        ref_no: receipt.receipt_no || receipt.id,
        transaction_type: 'credit',
        color: '#52c41a'
      });
    });
    let filtered = transactions;
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(t => {
        const tDate = new Date(t.date);
        return tDate >= dateRange[0] && tDate <= dateRange[1];
      });
    }
    if (selectedVehicle !== 'all') {
      filtered = filtered.filter(t => t.vehicle_no === selectedVehicle);
    }
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    return filtered;
  };

  const getFilteredBills = () => {
    let filtered = [...bills];
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(bill => {
        const billDate = new Date(bill.bill_date);
        return billDate >= dateRange[0] && billDate <= dateRange[1];
      });
    }
    if (selectedVehicle !== 'all') {
      filtered = filtered.filter(bill => bill.vehicle_no === selectedVehicle);
    }
    filtered.sort((a, b) => new Date(b.bill_date) - new Date(a.bill_date));
    return filtered;
  };

  const getFilteredReceipts = () => {
    let filtered = [...receipts];
    if (dateRange[0] && dateRange[1]) {
      filtered = filtered.filter(receipt => {
        const receiptDate = new Date(receipt.receipt_date);
        return receiptDate >= dateRange[0] && receiptDate <= dateRange[1];
      });
    }
    if (selectedVehicle !== 'all') {
      filtered = filtered.filter(receipt => receipt.vehicle_no === selectedVehicle);
    }
    filtered.sort((a, b) => new Date(b.receipt_date) - new Date(a.receipt_date));
    return filtered;
  };

  const calculateTotals = () => {
    const totalBills = bills.reduce((sum, b) => sum + (parseFloat(b.total_amount) || 0), 0);
    const totalReceipts = receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const balance = totalBills - totalReceipts;
    return { totalBills, totalReceipts, balance };
  };

  const exportToExcel = () => {
    setExportLoading(true);
    try {
      const transactions = getCombinedTransactions();
      if (transactions.length === 0) {
        message.warning("No data to export!");
        setExportLoading(false);
        return;
      }
      const excelData = transactions.map(t => ({
        "Date": t.date || '-',
        "Type": t.type,
        "Ref No": t.ref_no || '-',
        "Vehicle No": t.vehicleNo || '-',
        "Description": t.description || t.rec_category || '-',
        "Bill Amount": t.type === 'Bill' ? t.amount : '-',
        "Receipt Amount": t.type === 'Receipt' ? t.amount : '-',
        "Payment Mode": t.rec_mode || '-',
        "Status": t.type === 'Bill' ? 'Pending' : 'Received'
      }));
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${selectedParty?.partyName}_Ledger`);
      XLSX.writeFile(wb, `${selectedParty?.partyName}_Ledger_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) {
      console.error(err);
      message.error("Export failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const { totalBills, totalReceipts, balance } = calculateTotals();

  // ---------- Desktop Table Columns (unchanged) ----------
  const getAllTransactionsColumns = () => [
    { title: 'Date', dataIndex: 'date', key: 'date', width: 100, render: (text) => <Tag color={text?.includes('Bill') ? 'geekblue' : 'green'}>{formatDate(text)}</Tag> },
    { title: 'Type', dataIndex: 'type', key: 'type', width: 100, render: (text) => <Tag color={text === 'Bill' ? 'blue' : 'green'}>{text}</Tag> },
    { title: 'Ref No', dataIndex: 'ref_no', key: 'ref_no', render: (text) => <Text strong>{text || '—'}</Text> },
    { title: 'Vehicle No', dataIndex: 'vehicleNo', key: 'vehicleNo', render: (text) => text ? <Tag icon={<CarOutlined />} color="cyan">{text}</Tag> : '—' },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (text, record) => text || record.rec_category || '—' },
    { title: 'Bill Amount', dataIndex: 'amount', key: 'bill_amount', align: 'right', render: (text, record) => record.type === 'Bill' ? <Text strong style={{ color: '#1890ff' }}>{formatAmount(text)}</Text> : '-' },
    { title: 'Receipt Amount', dataIndex: 'amount', key: 'receipt_amount', align: 'right', render: (text, record) => record.type === 'Receipt' ? <Text strong style={{ color: '#52c41a' }}>{formatAmount(text)}</Text> : '-' },
    { title: 'Mode', dataIndex: 'rec_mode', key: 'mode', render: (text) => text ? <Tag icon={<CreditCardOutlined />} color="purple">{text}</Tag> : '-' }
  ];

  const getBillColumns = () => [
    { title: 'Date', dataIndex: 'bill_date', key: 'bill_date', width: 100, render: (text) => <Tag color="geekblue">{formatDate(text)}</Tag> },
    { title: 'Bill No', dataIndex: 'bill_id', key: 'bill_id', render: (text) => <Text strong>{text || '—'}</Text> },
    {
      title: 'Vehicle No',
      key: 'vehicleNo',
      render: (_, record) => (
        record.vehicle_no ||
        record.items?.[0]?.vehicle_no
      ) ? (
        <Tag icon={<CarOutlined />} color="cyan">
          {record.vehicle_no || record.items?.[0]?.vehicle_no}
        </Tag>
      ) : '—'
    },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (text) => text || '—' },
    { title: 'Amount', dataIndex: 'total_amount', key: 'total_amount', align: 'right', render: (text) => <Text strong style={{ color: '#1890ff' }}>{formatAmount(text)}</Text> }
  ];

  const getReceiptColumns = () => [
    { title: 'Date', dataIndex: 'receipt_date', key: 'receipt_date', width: 100, render: (text) => <Tag color="green">{formatDate(text)}</Tag> },
    { title: 'Receipt No', dataIndex: 'receipt_no', key: 'receipt_no', render: (text) => <Text strong>{text || '—'}</Text> },
    { title: 'Vehicle No', dataIndex: 'vehicle_no', key: 'vehicleNo', render: (text) => text ? <Tag icon={<CarOutlined />} color="cyan">{text}</Tag> : '—' },
    { title: 'Category', dataIndex: 'rec_category', key: 'rec_category', render: (text) => text || '—' },
    { title: 'Payment Mode', dataIndex: 'rec_mode', key: 'rec_mode', render: (text) => <Tag icon={<CreditCardOutlined />} color="purple">{text}</Tag> },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (text) => <Text strong style={{ color: '#52c41a' }}>{formatAmount(text)}</Text> }
  ];

  const getVehicleColumns = () => [
    { title: 'SR NO.', key: 'srno', width: 70, render: (_, __, index) => index + 1 },
    { title: 'Vehicle No', dataIndex: 'vehicleNo', key: 'vehicleNo', render: (text) => <Tag color="blue" icon={<CarOutlined />}>{text}</Tag> },
    { title: 'Chassis No', dataIndex: 'chassisNumber', key: 'chassisNumber', render: (text) => text || '—' },
    { title: 'Insurance Validity', key: 'insuranceValidity', render: (_, record) => { const expired = isExpired(record.insuranceValidity); return <div style={{ backgroundColor: expired ? '#ffccc7' : '#f6ffed', padding: '4px 8px', borderRadius: '4px' }}>{record.insuranceValidity || '—'}</div>; } },
    { title: 'Tax Validity', key: 'taxValidity', render: (_, record) => { const expired = isExpired(record.taxValidity); return <div style={{ backgroundColor: expired ? '#ffccc7' : '#f6ffed', padding: '4px 8px', borderRadius: '4px' }}>{record.taxValidity || '—'}</div>; } },
    { title: 'Fitness Validity', key: 'fitnessValidity', render: (_, record) => { const expired = isExpired(record.fitnessValidity); return <div style={{ backgroundColor: expired ? '#ffccc7' : '#f6ffed', padding: '4px 8px', borderRadius: '4px' }}>{record.fitnessValidity || '—'}</div>; } },
    { title: 'Pollution Validity', key: 'pollutionValidity', render: (_, record) => { const expired = isExpired(record.pollutionValidity); return <div style={{ backgroundColor: expired ? '#ffccc7' : '#f6ffed', padding: '4px 8px', borderRadius: '4px' }}>{record.pollutionValidity || '—'}</div>; } },
    {
      title: 'Actions', key: 'actions', width: 150, render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title="Edit"><Button type="link" size="small" icon={<EditOutlined />} onClick={() => onEditVehicle && onEditVehicle(record.id)} /></Tooltip> */}
          <Tooltip title="Download PDF"><Button type="link" size="small" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={() => downloadVehiclePDF(record)} loading={pdfLoading} /></Tooltip>
          <Popconfirm title="Delete Vehicle" description={`Delete ${record.vehicleNo}?`} onConfirm={() => handleVehicleDelete(record.vehicleNo, record.id)}>
            <Tooltip title="Delete"><Button type="link" danger size="small" icon={<DeleteOutlined />} /></Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // ---------- Mobile Card Renderers (Collapsible) ----------
  const renderTransactionCard = (item) => {
    const key = `trans_${item.id || item.ref_no}`;
    const isExpanded = expandedItems[key];
    return (
      <Card key={key} size="small" style={{ marginBottom: 12, borderRadius: 10 }} onClick={() => toggleExpand(item.id || item.ref_no, 'trans')} hoverable>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Tag color={item.type === 'Bill' ? 'blue' : 'green'}>{item.type}</Tag>
            <Text strong style={{ marginLeft: 8 }}>{formatDate(item.date)}</Text>
          </div>
          <div>
            {item.type === 'Bill' ?
              <Text strong style={{ color: '#1890ff' }}>{formatAmount(item.amount)}</Text> :
              <Text strong style={{ color: '#52c41a' }}>{formatAmount(item.amount)}</Text>
            }
            {isExpanded ? <UpOutlined style={{ marginLeft: 8 }} /> : <DownOutlined style={{ marginLeft: 8 }} />}
          </div>
        </div>
        {isExpanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Row gutter={[8, 8]}>
              <Col span={12}><Text type="secondary">Ref No:</Text> <Text>{item.ref_no || '—'}</Text></Col>
              <Col span={12}><Text type="secondary">Vehicle:</Text> {item.vehicleNo ? <Tag icon={<CarOutlined />} color="cyan">{item.vehicleNo}</Tag> : '—'}</Col>
              <Col span={24}><Text type="secondary">Description:</Text> <Text>{item.description || item.rec_category || '—'}</Text></Col>
              {item.rec_mode && <Col span={24}><Text type="secondary">Mode:</Text> <Tag icon={<CreditCardOutlined />} color="purple">{item.rec_mode}</Tag></Col>}
            </Row>
          </div>
        )}
      </Card>
    );
  };

  const renderBillCard = (bill) => {
    const key = `bill_${bill.id}`;
    const isExpanded = expandedItems[key];
    return (
      <Card key={key} size="small" style={{ marginBottom: 12, borderRadius: 10 }} onClick={() => toggleExpand(bill.id, 'bill')} hoverable>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Tag color="geekblue">{formatDate(bill.bill_date)}</Tag>
            <Text strong style={{ marginLeft: 8 }}>{bill.bill_id || '—'}</Text>
          </div>
          <div>
            <Text strong style={{ color: '#1890ff' }}>{formatAmount(bill.total_amount)}</Text>
            {isExpanded ? <UpOutlined style={{ marginLeft: 8 }} /> : <DownOutlined style={{ marginLeft: 8 }} />}
          </div>
        </div>
        {isExpanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Row gutter={[8, 8]}>
              <Col span={12}><Text type="secondary">Vehicle:</Text> {bill.vehicle_no || bill.items?.[0]?.vehicle_no ? <Tag icon={<CarOutlined />} color="cyan">{bill.vehicle_no || bill.items?.[0]?.vehicle_no}</Tag> : '—'}</Col>
              <Col span={24}><Text type="secondary">Description:</Text> <Text>{bill.description || '—'}</Text></Col>
            </Row>
          </div>
        )}
      </Card>
    );
  };

  const renderReceiptCard = (receipt) => {
    const key = `receipt_${receipt.id}`;
    const isExpanded = expandedItems[key];
    return (
      <Card key={key} size="small" style={{ marginBottom: 12, borderRadius: 10 }} onClick={() => toggleExpand(receipt.id, 'receipt')} hoverable>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Tag color="green">{formatDate(receipt.receipt_date)}</Tag>
            <Text strong style={{ marginLeft: 8 }}>{receipt.receipt_no || '—'}</Text>
          </div>
          <div>
            <Text strong style={{ color: '#52c41a' }}>{formatAmount(receipt.amount)}</Text>
            {isExpanded ? <UpOutlined style={{ marginLeft: 8 }} /> : <DownOutlined style={{ marginLeft: 8 }} />}
          </div>
        </div>
        {isExpanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Row gutter={[8, 8]}>
              <Col span={12}><Text type="secondary">Vehicle:</Text> {receipt.vehicle_no ? <Tag icon={<CarOutlined />} color="cyan">{receipt.vehicle_no}</Tag> : '—'}</Col>
              <Col span={12}><Text type="secondary">Category:</Text> <Text>{receipt.rec_category || '—'}</Text></Col>
              <Col span={12}><Text type="secondary">Mode:</Text> <Tag icon={<CreditCardOutlined />} color="purple">{receipt.rec_mode}</Tag></Col>
            </Row>
          </div>
        )}
      </Card>
    );
  };

  const renderVehicleCard = (vehicle) => {
    const key = `vehicle_${vehicle.id}`;
    const isExpanded = expandedItems[key];
    const expiredInsurance = isExpired(vehicle.insuranceValidity);
    const expiredTax = isExpired(vehicle.taxValidity);
    const expiredFitness = isExpired(vehicle.fitnessValidity);
    const expiredPollution = isExpired(vehicle.pollutionValidity);
    return (
      <Card key={key} size="small" style={{ marginBottom: 12, borderRadius: 10 }} onClick={() => toggleExpand(vehicle.id, 'vehicle')} hoverable>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <CarOutlined style={{ marginRight: 8 }} />
            <Text strong>{vehicle.vehicleNo}</Text>
          </div>
          <div>{isExpanded ? <UpOutlined /> : <DownOutlined />}</div>
        </div>
        {isExpanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
            <Row gutter={[8, 8]}>
              <Col span={12}><Text type="secondary">Chassis:</Text> <Text>{vehicle.chassisNumber || '—'}</Text></Col>
              <Col span={12}><Text type="secondary">Insurance:</Text> <div style={{ backgroundColor: expiredInsurance ? '#ffccc7' : '#f6ffed', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>{vehicle.insuranceValidity || '—'}</div></Col>
              <Col span={12}><Text type="secondary">Tax:</Text> <div style={{ backgroundColor: expiredTax ? '#ffccc7' : '#f6ffed', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>{vehicle.taxValidity || '—'}</div></Col>
              <Col span={12}><Text type="secondary">Fitness:</Text> <div style={{ backgroundColor: expiredFitness ? '#ffccc7' : '#f6ffed', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>{vehicle.fitnessValidity || '—'}</div></Col>
              <Col span={12}><Text type="secondary">Pollution:</Text> <div style={{ backgroundColor: expiredPollution ? '#ffccc7' : '#f6ffed', padding: '2px 6px', borderRadius: 4, display: 'inline-block' }}>{vehicle.pollutionValidity || '—'}</div></Col>
              <Col span={24}>
                <Space>
                  {/* <Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); onEditVehicle && onEditVehicle(vehicle.id); }}>Edit</Button> */}
                  <Button size="small" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={(e) => { e.stopPropagation(); downloadVehiclePDF(vehicle); }} loading={pdfLoading}>PDF</Button>
                  <Popconfirm title="Delete Vehicle" description={`Delete ${vehicle.vehicleNo}?`} onConfirm={(e) => { e?.stopPropagation(); handleVehicleDelete(vehicle.vehicleNo, vehicle.id); }}>
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>Delete</Button>
                  </Popconfirm>
                </Space>
              </Col>
            </Row>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', padding: isMobile ? 12 : 24 }}>

      {/* Header Card - Party Selection + Stats (3 in one line on mobile) */}
      <Card style={{ marginBottom: 24, borderRadius: 12 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size={isMobile ? 4 : 8}>
              <Text strong style={{ fontSize: isMobile ? 12 : 13 }}>Select Party</Text>
              <Select
                showSearch
                placeholder="Search and select party..."
                optionFilterProp="children"
                onChange={handlePartySelect}
                style={{ width: '100%' }}
                size={isMobile ? "middle" : "large"}
                allowClear
                filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
              >
                {parties.map(party => (
                  <Option key={party.id} value={party.id}>
                    <Space size={4}>
                      <UserOutlined />
                      <span>{party.partyName}</span>
                      {party.mobileNumber && !isMobile && <span style={{ color: '#999', fontSize: 12 }}>({party.mobileNumber})</span>}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          {selectedParty && (
            <Col xs={24} md={16}>
              {/* 3 stats always in one horizontal line: use flex with equal width */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                <Card style={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8, textAlign: 'center' }} bodyStyle={{ padding: '8px' }}>
                  <Statistic title={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? 10 : 14 }}>Total Bills</span>} value={totalBills} prefix="₹" valueStyle={{ color: 'white', fontSize: isMobile ? 14 : 20 }} />
                </Card>
                <Card style={{ flex: isMobile ? "unset" : 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8, textAlign: 'center' }} bodyStyle={{ padding: '8px' }}>
                  <Statistic title={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? 10 : 14 }}>Total Receipts</span>} value={totalReceipts} prefix="₹" valueStyle={{ color: 'white', fontSize: isMobile ? 14 : 20 }} />
                </Card>
                <Card style={{ flex: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 8, textAlign: 'center' }} bodyStyle={{ padding: '8px' }}>
                  <Statistic title={<span style={{ color: 'rgba(255,255,255,0.8)', fontSize: isMobile ? 10 : 14 }}>Balance</span>} value={balance} prefix="₹" valueStyle={{ color: balance >= 0 ? '#f6ffed' : '#ffccc7', fontSize: isMobile ? 14 : 20 }} />
                </Card>
              </div>
            </Col>
          )}
        </Row>
      </Card>

      {selectedParty && (
        <>
          {/* Filters */}
          <Card style={{ marginBottom: 24, borderRadius: 12 }} bodyStyle={{ padding: isMobile ? '12px 16px' : '16px 20px' }}>
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 8 }}><CalendarOutlined /> Date Range</Text>
                  <RangePicker style={{ width: '100%' }} onChange={(dates) => setDateRange(dates || [null, null])} placeholder={isMobile ? ['Start', 'End'] : ['Start Date', 'End Date']} size={isMobile ? "middle" : "large"} />
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div>
                  <Text strong style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 8 }}><CarOutlined /> Vehicle Filter</Text>
                  <Select style={{ width: '100%' }} value={selectedVehicle} onChange={setSelectedVehicle} allowClear size={isMobile ? "middle" : "large"}>
                    <Option value="all">All Vehicles</Option>
                    {vehicles.map(vehicle => <Option key={vehicle.vehicleNo} value={vehicle.vehicleNo}>{vehicle.vehicleNo}</Option>)}
                  </Select>
                </div>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Space wrap size={isMobile ? 8 : 16}>
                  <Button type="primary" icon={<DownloadOutlined />} onClick={exportToExcel} loading={exportLoading} size={isMobile ? "middle" : "large"}>{isMobile ? "Export" : "Export Ledger"}</Button>
                  <Button icon={<ClearOutlined />} onClick={() => { setDateRange([null, null]); setSelectedVehicle('all'); }} size={isMobile ? "middle" : "large"}>Clear Filters</Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Tabs */}
          <Card style={{ borderRadius: 12, overflowX: 'hidden' }} bodyStyle={{ padding: 0 }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              type="card"
              style={{ padding: isMobile ? '0 8px' : '0 16px' }}
              size={isMobile ? "small" : "middle"}
              items={[
                {
                  key: 'all',
                  label: <Space size={4}><FileTextOutlined />{isMobile ? "All" : "All Transactions"}</Space>,
                  children: isMobile ? (
                    <div style={{ padding: 12 }}>
                      <>
                        {getCombinedTransactions()
                          .slice(
                            (allPage - 1) * mobilePageSize,
                            allPage * mobilePageSize
                          )
                          .map(t => renderTransactionCard(t))}

                        <Pagination
                          current={allPage}
                          pageSize={mobilePageSize}
                          total={getCombinedTransactions().length}
                          onChange={setAllPage}
                          size="small"
                          showSizeChanger={false}
                          style={{ textAlign: "center", marginTop: 15 }}
                        />
                      </>
                    </div>
                  ) : (
                    <Table columns={getAllTransactionsColumns()} dataSource={getCombinedTransactions()} rowKey="id" loading={loading} pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} transactions`, showSizeChanger: true }} size="middle" bordered />
                  )
                },
                {
                  key: 'bills',
                  label: <Space size={4}><DollarOutlined />{isMobile ? "Bills" : "Bills Only"}</Space>,
                  children: isMobile ? (
                    <div style={{ padding: 12 }}>
                      {getFilteredBills()
                        .slice(
                          (billPage - 1) * mobilePageSize,
                          billPage * mobilePageSize
                        )
                        .map(b => renderBillCard(b))}

                      <Pagination
                        current={billPage}
                        pageSize={mobilePageSize}
                        total={getFilteredBills().length}
                        onChange={setBillPage}
                        size="small"
                        showSizeChanger={false}
                        style={{ textAlign: "center", marginTop: 15 }}
                      />
                    </div>
                  ) : (
                    <Table columns={getBillColumns()} dataSource={getFilteredBills()} rowKey="id" loading={loading} pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} bills`, showSizeChanger: true }} size="middle" bordered />
                  )
                },
                {
                  key: 'receipts',
                  label: <Space size={4}><WalletOutlined />{isMobile ? "Receipts" : "Receipts Only"}</Space>,
                  children: isMobile ? (
                    <div style={{ padding: 12 }}>
                      {getFilteredReceipts()
                        .slice(
                          (receiptPage - 1) * mobilePageSize,
                          receiptPage * mobilePageSize
                        )
                        .map(r => renderReceiptCard(r))}

                      <Pagination
                        current={receiptPage}
                        pageSize={mobilePageSize}
                        total={getFilteredReceipts().length}
                        onChange={setReceiptPage}
                        size="small"
                        showSizeChanger={false}
                        style={{ textAlign: "center", marginTop: 15 }}
                      />
                    </div>
                  ) : (
                    <Table columns={getReceiptColumns()} dataSource={getFilteredReceipts()} rowKey="id" loading={loading} pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} receipts`, showSizeChanger: true }} size="middle" bordered />
                  )
                },
                {
                  key: 'vehicles',
                  label: <Space size={4}><CarOutlined />{isMobile ? "Vehicles" : "Vehicles & Documents"}</Space>,
                  children: isMobile ? (
                    <div style={{ padding: 12 }}>
                      {vehicleReportData
                        .slice(
                          (vehiclePage - 1) * mobilePageSize,
                          vehiclePage * mobilePageSize
                        )
                        .map(v => renderVehicleCard(v))}

                      <Pagination
                        current={vehiclePage}
                        pageSize={mobilePageSize}
                        total={vehicleReportData.length}
                        onChange={setVehiclePage}
                        size="small"
                        showSizeChanger={false}
                        style={{ textAlign: "center", marginTop: 15 }}
                      />
                    </div>
                  ) : (
                    <Table columns={getVehicleColumns()} dataSource={vehicleReportData} rowKey="id" loading={vehicleLoading} pagination={{ pageSize: 10, showTotal: (total) => `Total ${total} vehicles`, showSizeChanger: true }} size="middle" bordered />
                  )
                }
              ]}
            />
          </Card>

          {/* Bottom Summary - two cards side by side */}
          <Card style={{ marginTop: 24, borderRadius: 12 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: isMobile ? 12 : 16, background: '#fafafa', borderRadius: 8 }}>
                  <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>Total Bill Amount</Text>
                  <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold', color: '#1890ff' }}>{formatAmount(totalBills)}</div>
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div style={{ textAlign: 'center', padding: isMobile ? 12 : 16, background: '#fafafa', borderRadius: 8 }}>
                  <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>Total Receipt Amount</Text>
                  <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 'bold', color: '#52c41a' }}>{formatAmount(totalReceipts)}</div>
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: isMobile ? '12px 0' : '16px 0' }} />
            <Row>
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: isMobile ? 12 : 16, background: balance >= 0 ? '#f6ffed' : '#fff1f0', borderRadius: 8 }}>
                  <Text strong style={{ fontSize: isMobile ? 14 : 16 }}>Outstanding Balance</Text>
                  <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 'bold', color: balance >= 0 ? '#52c41a' : '#ff4d4f' }}>
                    {formatAmount(Math.abs(balance))}
                    <span style={{ fontSize: isMobile ? 10 : 14, marginLeft: 8, display: isMobile ? 'block' : 'inline-block', marginTop: isMobile ? 4 : 0 }}>
                      {balance >= 0 ? '(Party owes you)' : '(You owe to party)'}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </>
      )}

      {!selectedParty && (
        <Card style={{ borderRadius: 12 }}>
          <Empty description={
            <div>
              <UserOutlined style={{ fontSize: isMobile ? 36 : 48, color: '#bfbfbf' }} />
              <p style={{ marginTop: 16, fontSize: isMobile ? 14 : 16 }}>Please select a party to view ledger</p>
              <p style={{ color: '#999', fontSize: isMobile ? 12 : 14 }}>Select a party from the dropdown above to see all bills, receipts, and vehicle documents</p>
            </div>
          } />
        </Card>
      )}
    </div>
  );
};

export default PartyLedger;